import { Component, inject, OnInit } from '@angular/core';
import { SecondaryPopupComponent } from '../../../../shared/components/secondary-popup/secondary-popup.component';
import { ConnectionDetailsComponent } from '../connection-details/connection-details.component';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { ManageConnectionService } from '../../services/manage-connection.service';
import { Connection } from '../../models/connection';
import { takeUntil, switchMap } from 'rxjs';
import { PaginationComponent } from '../../../../shared/components/pagination/pagination.component';
import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { FormsModule } from '@angular/forms';
import { of } from 'rxjs';
import { PageSizeComponent } from '../../../../shared/components/page-size/page-size.component';
import { CreateConnectionLink } from '../../models/create-connection-link';
import { Clipboard } from '@angular/cdk/clipboard';
import { DatePipe } from '@angular/common';
import { ConnectionLink } from '../../models/connection-link';
import { AuditLogService } from '../../../../shared/services/audit-log/audit-log.service';
import { ToastType } from '../../../../shared/interfaces/common';
import { CommonService } from '../../../../shared/services/common/common.service';
import {
  TableColumn,
  TableComponent,
} from '../../../../shared/components/table/table.component';

@Component({
  selector: 'app-manage-connection',
  standalone: true,
  imports: [
    SecondaryPopupComponent,
    ConnectionDetailsComponent,
    PopupComponent,
    PaginationComponent,
    FormsModule,
    PageSizeComponent,
    DatePipe,
    TableComponent,
  ],
  templateUrl: './manage-connection.component.html',
  styleUrl: './manage-connection.component.scss',
})
export class ManageConnectionComponent extends DestroyBase implements OnInit {
  manageConnectionService = inject(ManageConnectionService);
  clipboard = inject(Clipboard);
  auditLogService = inject(AuditLogService);
  private commonService = inject(CommonService);

  loading = true;
  connections: Connection[] = [];
  showConnectionDetailsPopup = false;
  showCreateConnectionPopup = false;
  currentPage = 1;
  pageSize = 50;
  totalConnections = 0;
  totalPages = 0;

  tableColumns: TableColumn[] = [
    { header: 'Connection Name' },
    { header: 'Strategy' },
    { header: 'Domain' },
    { header: 'Visibility' },
    { header: '' },
  ];

  errorMessage = '';
  selectedConnection!: Connection;
  creatingConnection = new Connection();
  validationErrors = new Connection();
  strategies: Record<string, string> = {
    waad: 'Microsoft Azure AD',
    'google-apps': 'Google Workspace',
  };
  strategyKeys = Object.keys(this.strategies);
  showConnectionModePopup = false;
  showConnectionLinkCreationPopup = false;
  creatingConnectionLink = new CreateConnectionLink();
  connectionLinkValidationErrors = new CreateConnectionLink();
  showGeneratedLinkPopup = false;
  generatedLink = '';
  connectionLinks: ConnectionLink[] = [];
  loadingLinks = false;
  CopyConfirmText = 'Copy';
  copiedLinkId: string | null = null;
  showConnectionLinksPopup = false;
  showDeleteLinkPopup = false;
  selectedConnectionLink!: ConnectionLink;
  showConnectionTypePopup = false;
  showDHUserConnectionPopup = false;
  selectedUserType = '';
  dhUserConnection = new Connection();
  dhUserValidationErrors = {
    name: '',
    display_name: '',
  };
  isAdminLinksExpanded = false;
  isSelfSignupLinksExpanded = false;

  get adminLinks(): ConnectionLink[] {
    return this.connectionLinks.filter((link) => !link.org_self_signup);
  }

  get selfSignupLinks(): ConnectionLink[] {
    return this.connectionLinks.filter((link) => link.org_self_signup);
  }

  getConnectionTypeLabel(connection: Connection): string {
    return connection.connection_type === 2 ? 'Data Hub' : 'Enterprise';
  }

  getVisibilityLabel(connection: Connection): string {
    return connection.hide ? 'Hidden' : 'Public';
  }

  getStrategyLabel(connection: Connection): string {
    if (connection.connection_type === 2) {
      return 'Data Hub User';
    }
    if (connection.strategy && this.strategies[connection.strategy]) {
      return this.strategies[connection.strategy];
    }
    return connection.strategy || 'Enterprise';
  }

  toggleAdminLinks(): void {
    this.isAdminLinksExpanded = !this.isAdminLinksExpanded;
  }

  toggleSelfSignupLinks(): void {
    this.isSelfSignupLinksExpanded = !this.isSelfSignupLinksExpanded;
  }

  ngOnInit(): void {
    this.loadConnections(this.currentPage, this.pageSize);
    this.loadConnectionLinks();
  }

  loadConnections(currentPage: number, pageSize: number): void {
    this.loading = true;
    this.manageConnectionService
      .getAllConnections(currentPage, pageSize)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.connections = res.results || [];
          this.totalConnections = res.count || 0;
          this.loading = false;
          const urlParams = new URLSearchParams(window.location.search);
          const connectionName = urlParams.get('connection');
          if (connectionName) {
            const connection = this.connections.find(
              (conn) =>
                conn.name === connectionName ||
                conn.display_name === connectionName ||
                conn.name.toLowerCase() === connectionName.toLowerCase() ||
                conn.display_name?.toLowerCase() ===
                  connectionName.toLowerCase(),
            );
            if (connection) {
              this.viewConnectionDetails(connection);
            } else {
              this.manageConnectionService
                .getConnectionDetails(connectionName)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                  next: (conn) => {
                    if (conn) {
                      this.viewConnectionDetails(conn);
                    }
                  },
                  error: (err) => {
                    console.error('Failed to load connection details:', err);
                  },
                });
            }
          }
        },
        error: (err) => {
          console.error('Failed to load connections:', err);
          this.loading = false;
        },
      });
  }

  loadConnectionLinks(): void {
    this.loadingLinks = true;
    this.manageConnectionService
      .getConnectionLinks()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (links) => {
          this.connectionLinks = links;
          this.loadingLinks = false;
        },
        error: () => {
          this.loadingLinks = false;
        },
      });
  }

  viewConnectionDetails(connection: Connection): void {
    this.selectedConnection = connection;
    const url = new URL(window.location.href);
    url.searchParams.set('connection', connection.name);
    window.history.pushState({}, '', url.toString());
    this.showConnectionDetailsPopup = true;
  }

  onDetailsPopupClose(isClickedOutside: boolean): void {
    if (!isClickedOutside) {
      const url = new URL(window.location.href);
      url.searchParams.delete('connection');
      window.history.pushState({}, '', url.toString());
      this.showConnectionDetailsPopup = false;
    }
  }

  openCreateConnectionPopup() {
    this.errorMessage = '';
    this.creatingConnection = new Connection();
    this.validationErrors = new Connection();
    this.showCreateConnectionPopup = true;
  }

  cancelCreateConnection() {
    this.showCreateConnectionPopup = false;
    this.creatingConnection = new Connection();
    this.validationErrors = new Connection();
    this.errorMessage = '';
  }

  openConnectionTypePopup() {
    this.showConnectionTypePopup = true;
  }

  openConnectionLinkCreationPopup() {
    this.errorMessage = '';
    this.creatingConnectionLink = new CreateConnectionLink();
    this.connectionLinkValidationErrors = new CreateConnectionLink();
    this.showConnectionLinksPopup = false;
    this.showConnectionLinkCreationPopup = true;
  }

  cancelConnectionLinkCreation() {
    this.showConnectionLinkCreationPopup = false;
    this.creatingConnectionLink = new CreateConnectionLink();
    this.connectionLinkValidationErrors = new CreateConnectionLink();
    this.errorMessage = '';
  }

  selectConnectionType(type: string) {
    this.showConnectionModePopup = false;
    if (type === 'manual') {
      this.openCreateConnectionPopup();
    } else if (type === 'link') {
      this.openConnectionLinkCreationPopup();
    }
  }

  selectUserType(userType: string) {
    this.showConnectionTypePopup = false;
    this.selectedUserType = userType;
    if (userType === 'DH') {
      this.showDHUserConnectionPopup = true;
    } else if (userType === 'Enterprise') {
      this.showConnectionModePopup = true;
    }
  }

  goBackToUserTypeSelection() {
    this.showConnectionModePopup = false;
    this.showConnectionTypePopup = true;
  }

  cancelDHUserConnection() {
    this.showDHUserConnectionPopup = false;
    // Clear the fields
    this.dhUserConnection = new Connection();
    // Clear validation errors
    this.dhUserValidationErrors = {
      name: '',
      display_name: '',
    };
    // Clear general error message
    this.errorMessage = '';
  }

  validateDHUserConnection(): boolean {
    let isValid = true;
    this.errorMessage = '';
    const connectionNamePattern = /^[a-zA-Z0-9](-[a-zA-Z0-9]|[a-zA-Z0-9])*$/;

    if (!this.dhUserConnection.name.trim()) {
      isValid = false;
      this.dhUserValidationErrors.name = 'Connection Name is required.';
    } else if (!connectionNamePattern.test(this.dhUserConnection.name)) {
      isValid = false;
      this.dhUserValidationErrors.name =
        'Connection Name must contain only letters, numbers, and hyphens. It cannot start or end with a hyphen.';
    } else {
      this.dhUserValidationErrors.name = '';
    }

    if (!this.dhUserConnection.display_name.trim()) {
      isValid = false;
      this.dhUserValidationErrors.display_name = 'Display Name is required.';
    } else {
      this.dhUserValidationErrors.display_name = '';
    }

    return isValid;
  }

  createDHUserConnection(): void {
    if (this.validateDHUserConnection()) {
      this.auditLogService.logFeature({
        featureName: 'connection',
        action: 'create_dh_user',
        meta: {
          connectionName: this.dhUserConnection.name,
          displayName: this.dhUserConnection.display_name,
        },
        description: 'User creates a new DH user connection',
      });

      this.dhUserConnection.connection_type = 2;
      this.manageConnectionService
        .createConnection(this.dhUserConnection)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res: Connection) => {
            this.dhUserConnection = new Connection();
            this.connections.push(res);
            this.totalConnections++;
            this.showDHUserConnectionPopup = false;
          },
          error: (err) => {
            this.errorMessage = err.error.message;
          },
        });
    }
  }

  onConnectionDelete(connection: Connection): void {
    this.showConnectionDetailsPopup = false;
    this.connections = this.connections.filter(
      (item) => item.id !== connection.id,
    );
    this.totalConnections--;
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadConnections(this.currentPage, this.pageSize);
  }

  onPageSizeChange(newPageSize: number): void {
    if (newPageSize !== this.pageSize) {
      this.pageSize = newPageSize;
      this.currentPage = 1;
      this.loadConnections(this.currentPage, this.pageSize);
    }
  }

  validateConnection(): boolean {
    let isValid = true;
    this.errorMessage = '';
    const connectionNamePattern = /^[a-zA-Z0-9](-[a-zA-Z0-9]|[a-zA-Z0-9])*$/;

    if (!this.creatingConnection.name.trim()) {
      isValid = false;
      this.validationErrors.name = 'Connection Name is required.';
    } else if (!connectionNamePattern.test(this.creatingConnection.name)) {
      isValid = false;
      this.validationErrors.name =
        'Connection Name must contain only letters, numbers, and hyphens. It cannot start or end with a hyphen.';
    } else {
      this.validationErrors.name = '';
    }

    if (!this.creatingConnection.display_name.trim()) {
      isValid = false;
      this.validationErrors.display_name = 'Display Name is required.';
    } else {
      this.validationErrors.display_name = '';
    }

    if (!this.creatingConnection.strategy.trim()) {
      isValid = false;
      this.validationErrors.strategy = 'Strategy is required.';
    } else {
      this.validationErrors.strategy = '';
    }

    if (!this.creatingConnection.domain.trim()) {
      isValid = false;
      this.validationErrors.domain = 'Domain is required.';
    } else {
      this.validationErrors.domain = '';
    }

    if (!this.creatingConnection.client_id.trim()) {
      isValid = false;
      this.validationErrors.client_id = 'Client ID is required.';
    } else {
      this.validationErrors.client_id = '';
    }

    if (!this.creatingConnection.client_secret.trim()) {
      isValid = false;
      this.validationErrors.client_secret = 'Client Secret is required.';
    } else {
      this.validationErrors.client_secret = '';
    }

    return isValid;
  }

  createConnection(): void {
    if (this.validateConnection()) {
      this.auditLogService.logFeature({
        featureName: 'connection',
        action: 'create',
        meta: {
          connectionName: this.creatingConnection.name,
          displayName: this.creatingConnection.display_name,
          strategy: this.creatingConnection.strategy,
        },
        description: 'User creates a new connection',
      });

      this.creatingConnection.connection_type = 1;
      this.manageConnectionService
        .createConnection(this.creatingConnection)
        .pipe(
          switchMap((res: Connection) => {
            this.showCreateConnectionPopup = false;
            this.connections.push(res);
            this.totalConnections++;
            this.selectedConnection = res;
            this.creatingConnection = new Connection();
            this.showConnectionDetailsPopup = true;
            return of(res);
          }),
          takeUntil(this.destroy$),
        )
        .subscribe({
          error: (res) => {
            this.errorMessage = res.error.message;
          },
        });
    }
  }

  validateConnectionLink(): boolean {
    let isValid = true;
    this.errorMessage = '';
    const connectionNamePattern = /^[a-zA-Z0-9](-[a-zA-Z0-9]|[a-zA-Z0-9])*$/;

    if (!this.creatingConnectionLink.connection_name.trim()) {
      isValid = false;
      this.connectionLinkValidationErrors.connection_name =
        'Connection Name is required.';
    } else if (
      !connectionNamePattern.test(this.creatingConnectionLink.connection_name)
    ) {
      isValid = false;
      this.connectionLinkValidationErrors.connection_name =
        'Connection Name must contain only letters, numbers, and hyphens. It cannot start or end with a hyphen.';
    } else {
      this.connectionLinkValidationErrors.connection_name = '';
    }

    if (!this.creatingConnectionLink.display_name.trim()) {
      isValid = false;
      this.connectionLinkValidationErrors.display_name =
        'Display Name is required.';
    } else {
      this.connectionLinkValidationErrors.display_name = '';
    }

    if (+this.creatingConnectionLink.validity < 1) {
      isValid = false;
      this.connectionLinkValidationErrors.validity =
        'Validity must be at least 1 day.';
    } else {
      this.connectionLinkValidationErrors.validity = '';
    }

    if (+this.creatingConnectionLink.allowed_visits < 1) {
      isValid = false;
      this.connectionLinkValidationErrors.allowed_visits =
        'Allowed visits must be at least 1.';
    } else {
      this.connectionLinkValidationErrors.allowed_visits = '';
    }

    return isValid;
  }

  createConnectionLink(): void {
    if (this.validateConnectionLink()) {
      this.auditLogService.logFeature({
        featureName: 'connection_link',
        action: 'create',
        meta: {
          connectionName: this.creatingConnectionLink.connection_name,
          displayName: this.creatingConnectionLink.display_name,
        },
        description: 'User creates a new connection link',
      });

      this.manageConnectionService
        .createConnectionLink(this.creatingConnectionLink)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (res) => {
            this.showConnectionLinkCreationPopup = false;
            this.generatedLink = res.generated_link;
            this.showGeneratedLinkPopup = true;
            this.creatingConnectionLink = new CreateConnectionLink();
            this.loadConnectionLinks(); // Refresh the links list
          },
          error: (err) => {
            this.errorMessage = err.error.message;
          },
        });
    }
  }

  copyGeneratedLink(value: string, linkId?: string): void {
    this.clipboard.copy(value);
    if (linkId) {
      this.copiedLinkId = linkId;
      setTimeout(() => {
        this.copiedLinkId = null;
      }, 2000);
    } else {
      this.CopyConfirmText = 'Copied!';
      setTimeout(() => {
        this.CopyConfirmText = 'Copy';
      }, 2000);
    }
  }

  closeGeneratedLinkPopup(): void {
    this.showGeneratedLinkPopup = false;
    this.generatedLink = '';
  }

  isVisitsExceeded(link: ConnectionLink): boolean {
    return link.visits >= link.allowed_visits;
  }

  isLinkValid(link: ConnectionLink): boolean {
    const now = new Date();
    const expiryDate = new Date(link.expiry);
    return expiryDate > now && link.visits < link.allowed_visits;
  }

  isLinkExpired(link: ConnectionLink): boolean {
    const now = new Date();
    const expiryDate = new Date(link.expiry);
    return expiryDate <= now;
  }

  viewConnectionLinks(): void {
    this.showConnectionLinksPopup = true;
    this.loadConnectionLinks();
  }

  onConnectionLinksPopupClose(): void {
    this.showConnectionLinksPopup = false;
  }

  recreateLink(link: ConnectionLink): void {
    const recreateConnectionLink = new CreateConnectionLink();
    recreateConnectionLink.connection_name = link.connection_name;
    recreateConnectionLink.display_name = link.display_name;
    recreateConnectionLink.validity = '1';
    recreateConnectionLink.allowed_visits = link.allowed_visits.toString();

    this.manageConnectionService
      .createConnectionLink(recreateConnectionLink)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const index = this.connectionLinks.findIndex((l) => l.id === link.id);
          if (index !== -1) {
            this.connectionLinks[index] = res;
          }
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Recreated connection link successfully!',
          });
        },
        error: (err) => {
          this.errorMessage = err.error.message;
        },
      });
  }

  onDeleteConnectionLink(link: ConnectionLink): void {
    this.showDeleteLinkPopup = true;
    this.selectedConnectionLink = link;
  }

  deleteLink(): void {
    this.auditLogService.logFeature({
      featureName: 'connection_link',
      action: 'delete',
      meta: {
        linkId: String(this.selectedConnectionLink.id),
        connectionName: this.selectedConnectionLink.connection_name,
      },
      description: 'User deletes a connection link',
    });

    this.manageConnectionService
      .deleteConnectionLink(this.selectedConnectionLink.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.connectionLinks = this.connectionLinks.filter(
            (l) => l.id !== this.selectedConnectionLink.id,
          );
          this.showDeleteLinkPopup = false;
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Deleted connection link successfully!',
          });
        },
        error: (err) => {
          this.errorMessage = err.error.message;
        },
      });
  }
}
